import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { Card, Badge, SectionHeader, Spinner } from '../../components/shared/UI';
import API from '../../utils/apiClient';

export default function MyTeacherPage() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [teacher, setTeacher] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get('/students/my-teacher')
      .then(({ data }) => setTeacher(data.teacher || null))
      .catch(() => addToast('Failed to load teacher info', 'error'))
      .finally(() => setLoading(false));
  }, [user]);

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
      <SectionHeader title="My Teacher" subtitle="Your assigned German instructor" />

      {loading ? (
        <div className="flex justify-center py-16"><Spinner size="lg" className="text-brand-500" /></div>
      ) : !teacher ? (
        <Card className="p-8 text-center">
          <p className="text-4xl mb-3">👨‍🏫</p>
          <h3 className="text-base font-bold text-de-black mb-2">No teacher assigned yet</h3>
          <p className="text-sm text-gray-500">
            Please contact the institute admin to be assigned to a teacher.
          </p>
          <div className="mt-4 p-3 bg-gold-50 border border-gold-400 rounded-xl">
            <p className="text-sm font-semibold text-de-black">Contact DeutschBridge Institute:</p>
            <a href="tel:0202071417" className="text-brand-500 font-bold text-sm">0202071417</a>
          </div>
        </Card>
      ) : (
        <Card className="overflow-hidden" accent="red">
          {/* Header */}
          <div className="p-6 border-b border-gray-100 bg-de-black text-white">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-brand-500 flex items-center justify-center text-2xl font-bold text-white flex-shrink-0">
                {teacher.name?.charAt(0)?.toUpperCase() || 'T'}
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">{teacher.name}</h2>
                <p className="text-gray-400 text-sm mt-0.5">German Language Instructor</p>
                <p className="text-gold-400 text-xs font-semibold mt-1">DeutschBridge Institute</p>
              </div>
            </div>
          </div>

          {/* Details */}
          <div className="p-6 space-y-4">
            {/* Email */}
            <div className="flex items-center justify-between p-4 bg-surface rounded-xl border border-gray-200">
              <div>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-0.5">Email</p>
                <a href={`mailto:${teacher.email}`}
                  className="text-sm font-semibold text-brand-500 hover:text-brand-700 transition-colors">
                  {teacher.email}
                </a>
              </div>
              <a href={`mailto:${teacher.email}`}
                className="flex items-center gap-1.5 px-3 py-2 bg-brand-500 text-white rounded-xl text-xs font-bold hover:bg-brand-600 transition-colors">
                ✉ Send Email
              </a>
            </div>

            {/* Phone */}
            {teacher.phone && (
              <div className="flex items-center justify-between p-4 bg-surface rounded-xl border border-gray-200">
                <div>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-0.5">Phone</p>
                  <a href={`tel:${teacher.phone}`}
                    className="text-sm font-semibold text-de-black hover:text-brand-500 transition-colors">
                    {teacher.phone}
                  </a>
                </div>
                <a href={`tel:${teacher.phone}`}
                  className="flex items-center gap-1.5 px-3 py-2 bg-de-black text-white rounded-xl text-xs font-bold hover:bg-gray-800 transition-colors">
                  📞 Call
                </a>
              </div>
            )}

            {/* Levels taught */}
            {teacher.assignedLevels?.length > 0 && (
              <div className="p-4 bg-surface rounded-xl border border-gray-200">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Teaches Levels</p>
                <div className="flex gap-2 flex-wrap">
                  {teacher.assignedLevels.map(l => (
                    <Badge key={l} variant="gold">{l}</Badge>
                  ))}
                </div>
              </div>
            )}

            {/* School contact */}
            <div className="mt-2 pt-4 border-t border-gray-100 text-center">
              <p className="text-xs text-gray-400">
                Have questions? Contact the institute at{' '}
                <a href="tel:0202071417" className="text-brand-500 font-semibold">0202071417</a>
              </p>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
